import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, GripVertical, Plus, Settings, Trash2 } from 'lucide-react';
import { getSupabaseClient, hasSupabaseConfig } from '../lib/supabaseClient';
import { formatSupabaseError } from '../lib/supabaseErrors';
import { useAuth } from '../auth/AuthContext';
import { useModuleAccess } from '../auth/useModuleAccess';
import {
  applyConsentToggleToAgenda,
  bylawsConfigFromOrganizationSettings,
  cloneAgendaTemplateFromOrder,
  createConsentLineItem,
  createStandardOrderOfBusiness,
  createStandardUnanimousConsentRequests,
  defaultOrganizationSettings,
  organizationSettingsFromBylaws,
} from '../utils/consentAgenda';
import SignedInUserLine from '../components/common/SignedInUserLine';
import OrgDocumentsPanel from '../components/org/OrgDocumentsPanel';

function reorderListItem(list, fromIndex, toIndex) {
  const items = [...(list || [])];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);
  return items;
}

export default function OrgSettingsPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { switchOrganization, ready, memberships, refreshMemberships } = useAuth();
  const { canAdmin } = useModuleAccess();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orgSettings, setOrgSettings] = useState(() =>
    defaultOrganizationSettings(createStandardOrderOfBusiness())
  );
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSavedAt, setSettingsSavedAt] = useState(null);
  const [orgNameDraft, setOrgNameDraft] = useState('');
  const [orgNameDirty, setOrgNameDirty] = useState(false);
  const [dragState, setDragState] = useState(null); // { list: 'agenda'|'uc', index: number }

  const membershipForOrg = memberships.find((m) => m.organization_id === orgId);

  useEffect(() => {
    if (!ready) return;
    if (!hasSupabaseConfig()) {
      navigate('/', { replace: true });
      return;
    }
    if (orgId) void switchOrganization(orgId);
  }, [ready, orgId, switchOrganization, navigate]);

  useEffect(() => {
    if (ready && orgId && membershipForOrg && !canAdmin) {
      navigate(`/orgs/${orgId}`, { replace: true });
    }
  }, [ready, orgId, canAdmin, membershipForOrg, navigate]);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase || !orgId) return;
    setError(null);
    try {
      const { data: bylaws, error: bylawsErr } = await supabase.rpc('get_org_bylaws_config', {
        p_organization_id: orgId,
      });
      if (bylawsErr) throw bylawsErr;
      setOrgSettings(organizationSettingsFromBylaws(bylaws));
      setSettingsDirty(false);
    } catch (err) {
      console.error('[OrgSettings] load failed', err);
      setError(formatSupabaseError(err, 'Failed to load organization settings'));
    }
  }, [orgId]);

  useEffect(() => {
    if (canAdmin && orgId && membershipForOrg) void load();
  }, [canAdmin, orgId, membershipForOrg, load]);

  useEffect(() => {
    const name = membershipForOrg?.organization_name || '';
    if (!orgNameDirty) {
      setOrgNameDraft(name);
    }
  }, [membershipForOrg?.organization_name, orgNameDirty]);

  const updateOrgSettings = (updater) => {
    setOrgSettings((prev) => (typeof updater === 'function' ? updater(prev) : updater));
    setSettingsDirty(true);
    setSettingsSavedAt(null);
  };

  const onDragStartRow = (list, index) => (e) => {
    setDragState({ list, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${list}:${index}`);
  };

  const onDragOverRow = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDropRow = (list, toIndex) => (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain') || '';
    const [dragList, fromRaw] = raw.split(':');
    const fromIndex = Number(fromRaw);
    if (dragList !== list || !Number.isFinite(fromIndex)) {
      setDragState(null);
      return;
    }
    if (list === 'agenda') {
      updateOrgSettings((prev) => ({
        ...prev,
        defaultOrderOfBusiness: reorderListItem(
          prev.defaultOrderOfBusiness,
          fromIndex,
          toIndex
        ),
      }));
    } else if (list === 'uc') {
      updateOrgSettings((prev) => ({
        ...prev,
        defaultUnanimousConsentRequests: reorderListItem(
          prev.defaultUnanimousConsentRequests,
          fromIndex,
          toIndex
        ),
      }));
    }
    setDragState(null);
  };

  const onDragEndRow = () => setDragState(null);

  const saveOrgSettings = async () => {
    const supabase = getSupabaseClient();
    if (!supabase || !orgId) return;
    setBusy(true);
    setError(null);
    try {
      const trimmedName = String(orgNameDraft || '').trim();
      if (orgNameDirty) {
        if (!trimmedName) {
          throw new Error('Organization name cannot be empty');
        }
        const { error: nameErr } = await supabase
          .from('organizations')
          .update({ name: trimmedName })
          .eq('id', orgId);
        if (nameErr) throw nameErr;
      }

      const payload = bylawsConfigFromOrganizationSettings(orgSettings);
      const { data, error: rpcErr } = await supabase.rpc('admin_update_org_bylaws', {
        p_organization_id: orgId,
        p_bylaws_config: payload,
      });
      if (rpcErr) throw rpcErr;
      setOrgSettings(organizationSettingsFromBylaws(data ?? payload));
      setSettingsDirty(false);
      setOrgNameDirty(false);
      setSettingsSavedAt(new Date());
      if (typeof refreshMemberships === 'function') {
        await refreshMemberships();
      }
    } catch (err) {
      setError(formatSupabaseError(err, 'Failed to save organization settings'));
    } finally {
      setBusy(false);
    }
  };

  const orgName = membershipForOrg?.organization_name || 'Organization';
  const saveDisabled = busy || (!settingsDirty && !orgNameDirty);

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line/80 bg-pp-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <Link
              to={`/orgs/${orgId}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-pp-muted hover:text-pp-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to {orgName}
            </Link>
            <h1 className="text-xl font-semibold tracking-tight">Organization settings</h1>
            <p className="text-sm text-pp-muted">
              Meeting defaults, bylaws uploads, consent, quorum, and UC presets
            </p>
            <SignedInUserLine className="mt-1" />
          </div>
          <Link
            to={`/orgs/${orgId}/admin`}
            className="rounded-xl border border-pp-line px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
          >
            Members &amp; invites
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-8 px-4 py-8 sm:px-6">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {orgId ? <OrgDocumentsPanel organizationId={orgId} /> : null}

        <section className="rounded-2xl border border-pp-line bg-pp-surface p-6 shadow-pp">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Settings className="h-4 w-4 text-pp-accent" />
                Meeting defaults
              </h2>
              <p className="mt-1 text-sm text-pp-muted">
                Applied when starting a new meeting. Chairs can still adjust this meeting&apos;s agenda,
                attendance, and quorum in the setup wizard.
              </p>
            </div>
            <button
              type="button"
              data-tour="settings-save"
              disabled={saveDisabled}
              onClick={() => void saveOrgSettings()}
              className="rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save settings'}
            </button>
          </div>
          {settingsSavedAt && !settingsDirty && !orgNameDirty && (
            <p className="mt-2 text-xs text-emerald-700">
              Saved {settingsSavedAt.toLocaleTimeString()}
            </p>
          )}

          <div className="mt-5" data-tour="settings-org-name">
            <h3 className="text-sm font-semibold">Organization name</h3>
            <p className="mt-1 text-xs text-pp-muted">
              Shown on org home, meeting headers, and the Chair Dashboard.
            </p>
            <input
              type="text"
              value={orgNameDraft}
              onChange={(e) => {
                setOrgNameDraft(e.target.value);
                setOrgNameDirty(true);
                setSettingsSavedAt(null);
              }}
              className="mt-2 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
              maxLength={120}
              placeholder="Organization name"
            />
          </div>

          <label
            data-tour="settings-consent"
            className="mt-5 flex cursor-pointer items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(orgSettings.allowConsentAgenda)}
              onChange={(e) => {
                const checked = e.target.checked;
                updateOrgSettings((prev) => ({
                  ...prev,
                  allowConsentAgenda: checked,
                  defaultOrderOfBusiness: applyConsentToggleToAgenda(
                    prev.defaultOrderOfBusiness || createStandardOrderOfBusiness(),
                    checked
                  ),
                  defaultConsentLines: checked ? prev.defaultConsentLines || [] : [],
                }));
              }}
            />
            <span>
              <span className="text-sm font-medium text-pp-ink">Enable consent calendar</span>
              <span className="mt-0.5 block text-xs text-pp-muted">
                When off, new meetings omit the consent calendar and floor consent tools stay hidden.
              </span>
            </span>
          </label>

          <label
            data-tour="settings-roll-call"
            className="mt-3 flex cursor-pointer items-start gap-2 rounded-xl border border-pp-line bg-pp-paper/60 p-3"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(orgSettings.enableRollCallRoster)}
              onChange={(e) => {
                const checked = e.target.checked;
                updateOrgSettings((prev) => ({
                  ...prev,
                  enableRollCallRoster: checked,
                }));
              }}
            />
            <span>
              <span className="text-sm font-medium text-pp-ink">
                Enable roll call roster in meetings
              </span>
              <span className="mt-0.5 block text-xs text-pp-muted">
                When the agenda reaches Roll Call, open a checklist of the Member roster so
                the chair can mark who is present.
              </span>
            </span>
          </label>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div
              data-tour="settings-parliamentary-mode"
              className="rounded-xl border border-pp-line/70 bg-pp-paper/60 p-3 opacity-60"
              aria-disabled="true"
            >
              <h3 className="text-sm font-semibold text-pp-muted">Default parliamentary mode</h3>
              <p className="mt-1 text-xs text-pp-muted">
                Applied to every new meeting session.
              </p>
              <select
                value={orgSettings.defaultParliamentaryMode || 'standard'}
                disabled
                className="mt-2 w-full cursor-not-allowed rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-muted"
              >
                <option value="standard">Standard (full RONR)</option>
                <option value="small_board">Small board</option>
                <option value="convention">Convention</option>
                <option value="committee">Committee</option>
              </select>
              <p className="mt-2 text-xs leading-snug text-pp-muted">
                Future feature — not currently functional. All meetings use the full motion console;
                changing this mode does not alter procedure yet.
              </p>
            </div>
            <div data-tour="settings-quorum">
              <h3 className="text-sm font-semibold">Default quorum</h3>
              <p className="mt-1 text-xs text-pp-muted">
                Pre-fills the meeting setup wizard; chairs can override for a session.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="org-quorum-type"
                    checked={(orgSettings.defaultQuorumType || 'percentage') === 'percentage'}
                    onChange={() => {
                      updateOrgSettings((prev) => ({
                        ...prev,
                        defaultQuorumType: 'percentage',
                      }));
                    }}
                  />
                  Percentage
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="org-quorum-type"
                    checked={orgSettings.defaultQuorumType === 'fixed'}
                    onChange={() => {
                      updateOrgSettings((prev) => ({
                        ...prev,
                        defaultQuorumType: 'fixed',
                      }));
                    }}
                  />
                  Fixed number
                </label>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={(orgSettings.defaultQuorumType || 'percentage') === 'percentage' ? 100 : undefined}
                  value={orgSettings.defaultQuorumValue ?? 50}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    updateOrgSettings((prev) => ({
                      ...prev,
                      defaultQuorumValue: Number.isFinite(value) ? value : 0,
                    }));
                  }}
                  className="w-28 rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                />
                <span className="text-sm text-pp-muted">
                  {(orgSettings.defaultQuorumType || 'percentage') === 'percentage' ? '%' : 'members'}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5" data-tour="settings-agenda">
            <h3 className="text-sm font-semibold">Default order of business</h3>
            <p className="mt-1 text-xs text-pp-muted">
              Drag the grip handle to rearrange. This order is used when starting a new meeting.
            </p>
            <ul className="mt-3 space-y-2">
              {(orgSettings.defaultOrderOfBusiness || []).map((row, index) => {
                const isDragging =
                  dragState?.list === 'agenda' && dragState?.index === index;
                return (
                <li
                  key={row.id ?? index}
                  onDragOver={onDragOverRow}
                  onDrop={onDropRow('agenda', index)}
                  className={`flex items-center gap-2 rounded-lg ${
                    isDragging ? 'opacity-50 ring-1 ring-pp-accent/40' : ''
                  }`}
                >
                  <button
                    type="button"
                    draggable
                    onDragStart={onDragStartRow('agenda', index)}
                    onDragEnd={onDragEndRow}
                    className="cursor-grab touch-none rounded border border-pp-line bg-pp-paper px-1.5 py-2 text-pp-muted active:cursor-grabbing"
                    aria-label={`Drag to reorder agenda item ${index + 1}`}
                    title="Drag to reorder"
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <span className="w-6 shrink-0 text-xs text-pp-muted">{index + 1}.</span>
                  <input
                    type="text"
                    value={row.title || ''}
                    onChange={(e) => {
                      const title = e.target.value;
                      updateOrgSettings((prev) => {
                        const list = cloneAgendaTemplateFromOrder(prev.defaultOrderOfBusiness);
                        list[index] = { ...list[index], title };
                        return { ...prev, defaultOrderOfBusiness: list };
                      });
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      updateOrgSettings((prev) => ({
                        ...prev,
                        defaultOrderOfBusiness: (prev.defaultOrderOfBusiness || []).filter(
                          (_, i) => i !== index
                        ),
                      }));
                    }}
                    className="rounded-lg border border-rose-200 px-2 py-1.5 text-xs text-rose-700"
                    aria-label="Remove agenda item"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={() => {
                updateOrgSettings((prev) => ({
                  ...prev,
                  defaultOrderOfBusiness: [
                    ...(prev.defaultOrderOfBusiness || []),
                    {
                      id: `agenda-${Date.now()}`,
                      type: 'standard',
                      title: 'New item',
                      completed: false,
                    },
                  ],
                }));
              }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              Add agenda item
            </button>
          </div>

          {orgSettings.allowConsentAgenda && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold">Default consent calendar lines</h3>
              <p className="mt-1 text-xs text-pp-muted">
                Copied into each new meeting; chairs can edit lines per meeting.
              </p>
              <ul className="mt-3 space-y-2">
                {(orgSettings.defaultConsentLines || []).map((line, index) => (
                  <li key={line.id ?? index} className="flex items-start gap-2">
                    <textarea
                      rows={2}
                      value={line.text || ''}
                      onChange={(e) => {
                        const text = e.target.value;
                        updateOrgSettings((prev) => {
                          const list = [...(prev.defaultConsentLines || [])];
                          list[index] = { ...list[index], text };
                          return { ...prev, defaultConsentLines: list };
                        });
                      }}
                      className="min-w-0 flex-1 rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                      placeholder="e.g. Approval of prior minutes"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        updateOrgSettings((prev) => ({
                          ...prev,
                          defaultConsentLines: (prev.defaultConsentLines || []).filter(
                            (_, i) => i !== index
                          ),
                        }));
                      }}
                      className="rounded-lg border border-rose-200 px-2 py-1.5 text-xs text-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  updateOrgSettings((prev) => ({
                    ...prev,
                    defaultConsentLines: [...(prev.defaultConsentLines || []), createConsentLineItem('')],
                  }));
                }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
              >
                <Plus className="h-3.5 w-3.5" />
                Add consent line
              </button>
            </div>
          )}

          <div className="mt-5">
            <h3 className="text-sm font-semibold">Standard unanimous consent requests</h3>
            <p className="mt-1 text-xs text-pp-muted">
              Quick-pick UC presets shown in every meeting. Drag to rearrange. Chairs can also type an
              ad hoc UC request on the floor.
            </p>
            <ul className="mt-3 space-y-2">
              {(orgSettings.defaultUnanimousConsentRequests || []).map((request, index) => {
                const isDragging = dragState?.list === 'uc' && dragState?.index === index;
                return (
                <li
                  key={`uc-${index}`}
                  onDragOver={onDragOverRow}
                  onDrop={onDropRow('uc', index)}
                  className={`flex items-center gap-2 rounded-lg ${
                    isDragging ? 'opacity-50 ring-1 ring-pp-accent/40' : ''
                  }`}
                >
                  <button
                    type="button"
                    draggable
                    onDragStart={onDragStartRow('uc', index)}
                    onDragEnd={onDragEndRow}
                    className="cursor-grab touch-none rounded border border-pp-line bg-pp-paper px-1.5 py-2 text-pp-muted active:cursor-grabbing"
                    aria-label={`Drag to reorder UC request ${index + 1}`}
                    title="Drag to reorder"
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    value={request}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateOrgSettings((prev) => {
                        const list = [...(prev.defaultUnanimousConsentRequests || [])];
                        list[index] = value;
                        return { ...prev, defaultUnanimousConsentRequests: list };
                      });
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                    placeholder="e.g. Recess for 5 minutes"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      updateOrgSettings((prev) => ({
                        ...prev,
                        defaultUnanimousConsentRequests: (
                          prev.defaultUnanimousConsentRequests || []
                        ).filter((_, i) => i !== index),
                      }));
                    }}
                    className="rounded-lg border border-rose-200 px-2 py-1.5 text-xs text-rose-700"
                    aria-label="Remove UC request"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
                );
              })}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  updateOrgSettings((prev) => ({
                    ...prev,
                    defaultUnanimousConsentRequests: [
                      ...(prev.defaultUnanimousConsentRequests || []),
                      '',
                    ],
                  }));
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
              >
                <Plus className="h-3.5 w-3.5" />
                Add UC request
              </button>
              <button
                type="button"
                onClick={() => {
                  updateOrgSettings((prev) => ({
                    ...prev,
                    defaultUnanimousConsentRequests: createStandardUnanimousConsentRequests(),
                  }));
                }}
                className="rounded-lg border border-pp-line px-3 py-1.5 text-xs font-medium"
              >
                Reset to built-in list
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
